/**
 * FRONT-3178: one app-level event stream per browser, not one per tab.
 *
 * Over HTTP/1.1 a browser allows six connections per host, shared by EVERY
 * tab of the profile. Each EDDY tab used to hold one of them forever for
 * `/api/events/<sessionId>`, so a handful of open tabs (plus a Build window's
 * tape stream or a streaming chat) used up the budget and every later request
 * from every tab — even `/api/health` — queued forever. Local dev is plain
 * HTTP/1.1 through Vite; production is HTTP/2, which multiplexes and is not
 * affected, but the per-tab stream was waste there too.
 *
 * The session id lives in localStorage, so every tab of one browser already
 * receives the identical stream. Tabs therefore elect a leader per session id
 * with a Web Lock: the holder opens the real stream and relays each event over
 * a BroadcastChannel; the others only listen. When the leader tab closes, the
 * browser releases its lock and the next waiting tab takes over and connects.
 * Every tab still runs each event through its own handler, so per-tab effects
 * (its own query cache, its own local-echo bookkeeping) are unchanged.
 */

export type RelayMessage =
  | { kind: 'event'; type: string; data: string }
  | { kind: 'session-lost' };

/** Opens the real stream, reporting every message through `emit`; returns its closer. */
export type OpenStream = (emit: (msg: RelayMessage) => void) => () => void;

interface LockLike {
  request(name: string, options: { signal: AbortSignal }, callback: () => Promise<void>): Promise<unknown>;
}

interface ChannelLike {
  onmessage: ((e: MessageEvent<RelayMessage>) => void) | null;
  postMessage(msg: RelayMessage): void;
  close(): void;
}

export interface SharedStreamDeps {
  locks?: LockLike;
  openChannel?: (name: string) => ChannelLike;
}

function browserDeps(): SharedStreamDeps {
  const locks = typeof navigator !== 'undefined' ? (navigator as { locks?: LockLike }).locks : undefined;
  const openChannel =
    typeof BroadcastChannel !== 'undefined' ? (name: string) => new BroadcastChannel(name) as ChannelLike : undefined;
  return { locks, openChannel };
}

/**
 * Receive the stream for `key` in this tab, opening it only if this tab wins
 * the lock. Without Web Locks or BroadcastChannel it falls back to opening the
 * stream directly — the old one-per-tab behaviour. Returns a cleanup.
 */
export function subscribeSharedStream(
  key: string,
  open: OpenStream,
  onMessage: (msg: RelayMessage) => void,
  deps: SharedStreamDeps = browserDeps(),
): () => void {
  const { locks, openChannel } = deps;
  if (!locks || !openChannel) return open(onMessage);

  const name = `eddy-events:${key}`;
  const channel = openChannel(name);
  const abort = new AbortController();
  let leading = false;
  let release: (() => void) | undefined;

  channel.onmessage = (e) => {
    if (!leading) onMessage(e.data);
  };

  locks
    .request(name, { signal: abort.signal }, async () => {
      if (abort.signal.aborted) return;
      leading = true;
      const close = open((msg) => {
        onMessage(msg);
        channel.postMessage(msg);
      });
      await new Promise<void>((resolve) => {
        release = resolve;
      });
      close();
    })
    .catch(() => {
      /* aborted while still waiting for the lock — this tab unmounted */
    });

  return () => {
    abort.abort();
    leading = false;
    release?.();
    channel.onmessage = null;
    channel.close();
  };
}
